import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { serverGetServerTime } from "../../api/api-server-data";
import { createId } from "../../shared/utils";
import { retryOnNetworkError } from "../api";
import { logInfoWithDebugFlag } from "../logger";

const STALE_TICK_MS = 3000;

interface TimeRemaining {
  timeRemainingMs: number | null;
  timeRemainingStr: string; // ex. "9:30"
  timeRemainingStrPadded: string; // ex. "09:30" when < 1hr, "01:09:30" when >= 1hr
}

type TimeUpdateCallback = (currentTimeMs: number | null) => void;

// Global state object containing all time tracking state
export const timeTrackingState = {
  globalInterval: null as NodeJS.Timeout | null,
  serverTimeOffsetMs: null as number | null,
  isSyncingServerTime: false,
  timeUpdateCallbacks: new Map<string, TimeUpdateCallback>(),
  lastGlobalTickAt: null as number | null,
};

const formatEndTimeForLog = (endTimeMs: number | null) =>
  endTimeMs === null ? null : new Date(endTimeMs).toISOString();

const logTimerSubscribe = (subscriptionId: string, endTimeMs: number | null) => {
  logInfoWithDebugFlag("debug_virtual_class", "[useTimeRemaining] subscribe", {
    subscriptionId,
    endTimeMs: formatEndTimeForLog(endTimeMs),
    callbackCount: timeTrackingState.timeUpdateCallbacks.size,
    hasServerTimeOffset: getServerTimeOffset() !== null,
  });
};

const logTimerUnsubscribe = (subscriptionId: string, endTimeMs: number | null, reason: string) => {
  logInfoWithDebugFlag("debug_virtual_class", "[useTimeRemaining] unsubscribe", {
    subscriptionId,
    endTimeMs: formatEndTimeForLog(endTimeMs),
    reason,
    callbackCountAfter: timeTrackingState.timeUpdateCallbacks.size,
  });
};

const subscribeToTimeUpdates = (
  subscriptionId: string,
  endTimeMs: number,
  callback: TimeUpdateCallback,
): void => {
  timeTrackingState.timeUpdateCallbacks.set(subscriptionId, callback);
  logTimerSubscribe(subscriptionId, endTimeMs);
};

const unsubscribeFromTimeUpdates = (
  subscriptionId: string,
  endTimeMs: number | null,
  reason: string,
): void => {
  timeTrackingState.timeUpdateCallbacks.delete(subscriptionId);
  logTimerUnsubscribe(subscriptionId, endTimeMs, reason);
};

const DEFAULT_TIME_REMAINING: TimeRemaining = {
  timeRemainingMs: null,
  timeRemainingStr: "0",
  timeRemainingStrPadded: "00:00",
};

// Export for testing purposes only
export const resetTimeTrackingState = (): void => {
  if (timeTrackingState.globalInterval) {
    clearInterval(timeTrackingState.globalInterval);
    timeTrackingState.globalInterval = null;
  }
  timeTrackingState.serverTimeOffsetMs = null;
  timeTrackingState.isSyncingServerTime = false;
  timeTrackingState.timeUpdateCallbacks.clear();
  timeTrackingState.lastGlobalTickAt = null;

  // Restart the interval after reset
  startGlobalInterval();
};

// ============================================================================
// FORMATTING HELPERS
// ============================================================================

export const padWithZero = (num: number) => {
  return String(num).padStart(2, "0");
};

const formatTimeRemaining = (timeRemainingMs: number | null) => {
  const { hours, minutes, seconds } = convertTime(timeRemainingMs);

  // Format time remaining string (e.g., "9:30", "1:23:45")
  let timeRemainingStr: string;
  if (hours > 0) {
    timeRemainingStr = `${hours}:${padWithZero(minutes)}:${padWithZero(seconds)}`;
  } else if (minutes > 0) {
    timeRemainingStr = `${minutes}:${padWithZero(seconds)}`;
  } else {
    timeRemainingStr = `${seconds}`;
  }

  // Format padded time string: HH:MM:SS when >= 1hr, else MM:SS when < 1hr (e.g., "05:00", "01:09:30")
  const timeRemainingStrPadded =
    hours > 0
      ? `${padWithZero(hours)}:${padWithZero(minutes)}:${padWithZero(seconds)}`
      : `${padWithZero(minutes)}:${padWithZero(seconds)}`;

  return { timeRemainingStr, timeRemainingStrPadded };
};

// ============================================================================
// TIME HELPERS
// ============================================================================

const convertTime = (timeRemainingMs: number | null) => {
  const totalSeconds = timeRemainingMs ? Math.floor(timeRemainingMs / 1000) : 0;
  const seconds = Math.floor(totalSeconds % 60);
  const minutes = Math.floor(totalSeconds / 60) % 60;
  const hours = Math.floor(totalSeconds / (60 * 60));
  return { hours, minutes, seconds };
};

const getLocalTime = (): number => {
  return Date.now();
};

const getServerTimeOffset = (): number | null => {
  return timeTrackingState.serverTimeOffsetMs;
};

export const getCurrentServerTimeMs = (): number | null => {
  const offsetMs = getServerTimeOffset();
  if (offsetMs === null) return null;
  return getLocalTime() + offsetMs;
};

/**
 * Initializes the server time offset by fetching the current server time
 * and calculating the offset from local time.
 *
 * Steps:
 * 1. Take the server time we received (serverTimeMs)
 * 2. Add half the round-trip time to compensate for network latency (roundTripTimeMs / 2)
 * 3. Subtract the local time at request to get the offset
 *
 * Example: If server time was 1000ms when we requested it, roundTripTimeMs was 50ms,
 * and local time was 500ms when we made the request:
 * serverTimeOffsetMs = (1000 + 25) - 500 = 525ms
 * Later, when local time is 1500ms, current server time ≈ 1500 + 525 = 2025ms
 */
const syncServerTime = async (): Promise<void> => {
  if (getServerTimeOffset() !== null || timeTrackingState.isSyncingServerTime) return;

  timeTrackingState.isSyncingServerTime = true;

  try {
    let fetchStartTime = 0;
    const data = await retryOnNetworkError(
      async () => {
        fetchStartTime = performance.now();
        return serverGetServerTime();
      },
      "serverTime",
      3,
    );

    const fetchEndTime = performance.now();
    const roundTripTimeMs = fetchEndTime - fetchStartTime;

    const serverTimeMs = data.server_time;
    const localTimeMs = getLocalTime();
    const _serverTimeOffsetMs = serverTimeMs + roundTripTimeMs / 2.0 - localTimeMs;

    timeTrackingState.serverTimeOffsetMs = _serverTimeOffsetMs;
  } catch (error) {
    console.error("Failed to initialize server time:", error);
  } finally {
    timeTrackingState.isSyncingServerTime = false;
  }
};

const stopGlobalInterval = (): void => {
  if (timeTrackingState.globalInterval) {
    clearInterval(timeTrackingState.globalInterval);
    timeTrackingState.globalInterval = null;
  }
};

const notifyAllTimeUpdateCallbacks = (reason: string): void => {
  const currentServerTimeMs = getCurrentServerTimeMs();
  logInfoWithDebugFlag(
    "debug_virtual_class_timer",
    "[useTimeRemaining] notifying subscribers of current offset",
    {
      callbackCount: timeTrackingState.timeUpdateCallbacks.size,
      currentServerTimeMs,
      reason,
    },
  );

  if (timeTrackingState.timeUpdateCallbacks.size === 0) return;

  timeTrackingState.timeUpdateCallbacks.forEach((callback) => callback(currentServerTimeMs));
  timeTrackingState.lastGlobalTickAt = Date.now();
};

const logTimerRepair = (
  reason: string,
  globalIntervalWasNull: boolean,
  wasStale: boolean,
): void => {
  logInfoWithDebugFlag("debug_virtual_class", "[useTimeRemaining] timer repair", {
    reason,
    callbackCount: timeTrackingState.timeUpdateCallbacks.size,
    lastGlobalTickAt: timeTrackingState.lastGlobalTickAt,
    globalIntervalWasNull,
    wasStale,
    action: wasStale ? "restarted stale global interval" : "restarted global interval",
  });
};

const ensureTimerTicking = (reason: string): void => {
  if (timeTrackingState.timeUpdateCallbacks.size === 0) return;

  const globalIntervalWasNull = timeTrackingState.globalInterval === null;

  if (globalIntervalWasNull) {
    startGlobalInterval();
    logTimerRepair(reason, true, false);

    notifyAllTimeUpdateCallbacks(reason);
  } else if (
    document.visibilityState === "visible" &&
    timeTrackingState.lastGlobalTickAt !== null &&
    Date.now() - timeTrackingState.lastGlobalTickAt > STALE_TICK_MS
  ) {
    stopGlobalInterval();
    startGlobalInterval();
    logTimerRepair(reason, false, true);

    notifyAllTimeUpdateCallbacks(reason);
  }
};

/**
 * Starts the global interval that notifies all registered subscribers
 * with the current adjusted server time every second.
 */
const startGlobalInterval = (): void => {
  if (timeTrackingState.globalInterval) return;

  const intervalCallback = () => {
    notifyAllTimeUpdateCallbacks("global interval tick");
  };

  timeTrackingState.globalInterval = setInterval(intervalCallback, 1000);
};

// TODO: check if each of these listeners are needed.  Visibility in particular
// is probably overkill
const handleDocumentVisible = (): void => {
  if (document.visibilityState !== "visible") {
    logInfoWithDebugFlag(
      "debug_virtual_class",
      "[useTimeRemaining] tab not visible, skipping global interval tick",
    );

    return;
  }

  ensureTimerTicking("tab visible");
};

const handlePageShow = (event: PageTransitionEvent): void => {
  if (!event.persisted) return;

  ensureTimerTicking("pageshow bfcache");
};

const handleWindowFocus = (): void => {
  ensureTimerTicking("window focus");
};

/**
 * Returns the remaining time until a given end date.
 *
 * @param endDate - Target end date to count down to, or null to show zero time
 * @returns Remaining time in ms plus formatted strings
 */
export const useTimeRemaining = (endDate: Date | null): TimeRemaining => {
  const [timeRemaining, setTimeRemaining] = useState<TimeRemaining>(DEFAULT_TIME_REMAINING);
  const subscriptionId = useRef<string | null>(null);
  const callbackRef = useRef<TimeUpdateCallback | null>(null);
  const endTimeMs = endDate?.getTime() ?? null;

  useEffect(() => {
    if (getServerTimeOffset() === null) {
      syncServerTime();
    }

    if (!endTimeMs) {
      setTimeRemaining(DEFAULT_TIME_REMAINING);

      if (subscriptionId.current) {
        unsubscribeFromTimeUpdates(subscriptionId.current, null, "endTimeMs cleared");
        subscriptionId.current = null;
        callbackRef.current = null;
      }

      return;
    }

    const callback = (currentServerTimeMs: number | null) => {
      if (currentServerTimeMs === null) {
        setTimeRemaining(DEFAULT_TIME_REMAINING);
        return;
      }

      const _timeRemainingMs = endTimeMs - currentServerTimeMs;
      const timeRemainingMs = _timeRemainingMs > 0 ? _timeRemainingMs : null;

      const { timeRemainingStr, timeRemainingStrPadded } = formatTimeRemaining(timeRemainingMs);

      setTimeRemaining({
        timeRemainingMs,
        timeRemainingStr,
        timeRemainingStrPadded,
      });
    };

    callbackRef.current = callback;
    subscriptionId.current = createId();
    subscribeToTimeUpdates(subscriptionId.current, endTimeMs, callback);

    callback(getCurrentServerTimeMs());
    ensureTimerTicking("useTimeRemaining subscribe");

    return () => {
      if (subscriptionId.current) {
        unsubscribeFromTimeUpdates(subscriptionId.current, endTimeMs, "effect cleanup");
        subscriptionId.current = null;
        callbackRef.current = null;
      }
    };
  }, [endTimeMs]);

  // While this runs on every render, it only does anything in the rare care where
  // we lost a callback and need to re-subscribe it.
  useLayoutEffect(() => {
    if (
      !endTimeMs ||
      !subscriptionId.current ||
      !callbackRef.current ||
      timeTrackingState.timeUpdateCallbacks.has(subscriptionId.current)
    )
      return;

    console.warn("[useTimeRemaining] lost callback, re-subscribing", {
      subscriptionId: subscriptionId.current,
      endTimeMs: formatEndTimeForLog(endTimeMs),
      callbackCount: timeTrackingState.timeUpdateCallbacks.size,
    });

    subscribeToTimeUpdates(subscriptionId.current, endTimeMs, callbackRef.current);

    callbackRef.current(getCurrentServerTimeMs());
  });

  return timeRemaining;
};

if (typeof window !== "undefined") {
  document.addEventListener("visibilitychange", handleDocumentVisible);
  window.addEventListener("pageshow", handlePageShow);
  window.addEventListener("focus", handleWindowFocus);

  window.addEventListener("beforeunload", () => {
    stopGlobalInterval();
  });
}

// Start the global interval immediately when module loads
startGlobalInterval();
